import { useRef } from "react";

// Type for each item in ecommerce.items
export interface GTMItem {
  item_id?: string;
  item_name?: string;
  affiliation?: string;
  coupon?: string;
  currency?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  quantity?: number;
}

// Shape of the ecommerce object
export interface EcommerceData {
  transaction_id?: string;
  affiliation?: string;
  currency?: string;
  value?: number;
  tax?: number;
  shipping?: number;
  coupon?: string;
  items?: GTMItem[];
  promotion_id?: string;
  promotion_name?: string;
  payment_type?: string;
  shipping_tier?: string;
  professional_credentials?: string;
}

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

if (!window.dataLayer) {
  window.dataLayer = [];
}

export default function useGTM() {
  const gtm = useRef<EcommerceData>({});

  const updateEcommerce = (partial: Partial<EcommerceData>) => {
    const hasItems = !!partial?.items?.length;

    gtm.current = {
      ...gtm.current,
      ...partial,
      items: hasItems ? partial.items : gtm.current.items,
    };
  };

  const beginCheckout = () => {
    window.dataLayer.push({ ecommerce: null });
    const obj = {
      event: "begin_checkout",
      ecommerce: gtm.current,
    };
    window.dataLayer.push(obj);
  };

  function addProfessionalCredentials({
    professional_credentials,
  }: {
    professional_credentials: string;
  }) {
    updateEcommerce({ professional_credentials });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "add_professional_credentials",
      ecommerce: gtm.current,
    });
  }

  function addToCart({ items }: { items: GTMItem[] }) {
    updateEcommerce({ items });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: gtm.current,
    });
  }

  function addShippingInfo({ shipping_tier }: { shipping_tier: string }) {
    updateEcommerce({ shipping_tier });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "add_shipping_info",
      ecommerce: gtm.current,
    });
  }

  // add coupon at items level
  function addPromoCode({
    promotion_id,
    promotion_name,
    coupon,
  }: {
    promotion_id: string;
    promotion_name: string;
    coupon: string;
  }) {
    const currentItems = gtm.current.items ?? [];

    // Add the coupon to each item individually
    const updatedItems = currentItems.map((item) => ({
      ...item,
      coupon,
    }));

    updateEcommerce({
      promotion_id,
      promotion_name,
      items: updatedItems,
    });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "select_promotion",
      ecommerce: gtm.current,
    });
  }

  function addPaymentInfo({ payment_type }: { payment_type: string }) {
    updateEcommerce({ payment_type });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "add_payment_info",
      ecommerce: gtm.current,
    });
  }

  function purchase({
    transaction_id,
    shipping,
    tax,
  }: {
    transaction_id: string;
    shipping: number;
    tax: number;
  }) {
    updateEcommerce({ transaction_id, shipping, tax });

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "purchase",
      ecommerce: gtm.current,
    });
  }

  return {
    updateEcommerce,
    beginCheckout,
    addProfessionalCredentials,
    addToCart,
    addShippingInfo,
    addPromoCode,
    addPaymentInfo,
    purchase,
  };
}
